import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Search,
  MessageSquare,
  Pin,
  MoreVertical,
  Trash2,
  Edit2,
  Share2,
  Check,
  ChevronDown,
  Plug,
  Sparkles,
  Compass,
  Cpu,
  ShieldCheck,
  Wrench,
  Layers,
  Calculator,
  ShoppingBag,
  Settings,
  Activity,
  Globe,
  HelpCircle,
  ArrowUpCircle,
  Brain,
  Info,
  LifeBuoy,
  LogOut
} from 'lucide-react';

const ENGINEERS_DATA = [
  { id: 'cost', name: 'Costing', tone: 'blue', icon: Calculator, avail: true },
  { id: 'dfm', name: 'DFM', tone: 'teal', icon: ShieldCheck, avail: true },
  { id: 'rnd', name: 'R&D', tone: 'purple', icon: Sparkles, hire: [1400, 21000], rent: [350, 5250] },
  { id: 'design', name: 'Design', tone: 'blue', icon: Compass, hire: [1800, 27000], rent: [450, 6750] },
  { id: 'sim', name: 'Simulation', tone: 'indigo', icon: Cpu, hire: [1600, 24000], rent: [400, 6000] },
  { id: 'mfg', name: 'Manufacturing', tone: 'slate', icon: Wrench, hire: [1200, 18000], rent: [300, 4500] },
  { id: 'qa', name: 'Quality', tone: 'rose', icon: Layers, hire: [1000, 15000], rent: [250, 3750] },
  { id: 'proc', name: 'Procurement', tone: 'amber', icon: ShoppingBag, hire: [1100, 16500], rent: [280, 4200] }
];

const TONE_STYLES = {
  blue: { bg: 'var(--blue-tint)', fg: 'var(--blue)' },
  teal: { bg: 'var(--green-tint)', fg: 'var(--green-ink)' },
  amber: { bg: 'var(--amber-tint)', fg: 'var(--amber)' },
  purple: { bg: 'var(--purple-tint)', fg: 'var(--purple)' },
  indigo: { bg: 'var(--indigo-tint)', fg: 'var(--indigo)' },
  rose: { bg: 'var(--rose-tint)', fg: 'var(--rose)' },
  slate: { bg: 'var(--slate-tint)', fg: 'var(--slate)' }
};

const DEFAULT_CONNECTORS = [
  { name: 'SolidWorks', mono: 'SW', color: '#C8102E' },
  { name: 'Mastercam', mono: 'MC', color: '#D4820A' },
  { name: 'Autodesk Fusion', mono: 'AF', color: '#0696D7' }
];

export default function Sidebar({
  conversations = [],
  activeChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  onPinChat,
  onRenameChat,
  currentUser,
  onOpenAuth,
  onSignOut,
  onOpenConnectors,
  onOpenHireModal,
  onToggleMemory,
  activeChain = null
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isChatsExpanded, setIsChatsExpanded] = useState(false);
  const [isEngExpanded, setIsEngExpanded] = useState(false);
  const [isAcctOpen, setIsAcctOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({
    recent: false,
    eng: false,
    conn: false
  });
  const [menuOpenChatId, setMenuOpenChatId] = useState(null);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const menuRef = useRef(null);
  const acctRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenChatId(null);
      }
      if (acctRef.current && !acctRef.current.contains(e.target)) {
        setIsAcctOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleGroup = (grp) => {
    setCollapsedGroups((prev) => ({ ...prev, [grp]: !prev[grp] }));
  };

  const handleStartRename = (chat) => {
    setRenamingChatId(chat.id);
    setRenameValue(chat.title);
    setMenuOpenChatId(null);
  };

  const handleSaveRename = (chatId) => {
    if (renameValue.trim()) {
      onRenameChat(chatId, renameValue.trim());
    }
    setRenamingChatId(null);
  };

  const handleShare = (chat) => {
    setMenuOpenChatId(null);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}/chat/${chat.id}`);
    }
    setToastMessage('Link copied to clipboard!');
    setTimeout(() => setToastMessage(''), 2500);
  };

  const filteredChats = conversations.filter(
    (c) =>
      !c.isArchived &&
      c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedChats = isChatsExpanded ? filteredChats : filteredChats.slice(0, 4);

  const activeChainSet = activeChain ? new Set(activeChain) : null;
  const displayedEngineers =
    !activeChain && !isEngExpanded
      ? ENGINEERS_DATA.slice(0, 3)
      : ENGINEERS_DATA;

  const displayName = currentUser?.name || currentUser?.email?.split('@')[0] || 'Rahul';
  const initialLetter = displayName.charAt(0).toUpperCase();

  return (
    <aside className="w-[244px] shrink-0 h-full bg-white border-r border-slate-200 flex flex-col select-none relative z-30 font-sans">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="absolute top-16 left-3 right-3 z-50 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-lg flex items-center justify-between animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-emerald-400" />
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* 1. Rail Top / Brand Header */}
      <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-slate-100 shrink-0">
        <div className="w-[38px] h-[38px] rounded-[10px] bg-[#0C64E8] flex items-center justify-center shrink-0 shadow-[0_2px_6px_rgba(12,100,232,0.35)]">
          {/* Isometric 3D Cube Icon */}
          <svg
            className="w-5 h-5 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="1.7"
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <path d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7z" />
            <path d="M12 2.5V12M12 12l8.5-5M12 12l-8.5-5M12 12v9.5" />
          </svg>
        </div>
        <div className="flex flex-col leading-tight min-w-0">
          <b className="text-[15px] font-semibold text-slate-900 tracking-tight">Dashnode</b>
          <span className="text-[11px] text-[#8193A8]">Engineering workspace</span>
        </div>
      </div>

      {/* 2. Scrollable Rail Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden pb-2 space-y-1">
        {/* New Task Button */}
        <div className="p-3 pb-2">
          <button
            type="button"
            onClick={onNewChat}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-[9px] font-semibold text-[13px] bg-[#0C64E8] hover:bg-[#0A4FBB] text-white shadow-[0_2px_6px_rgba(12,100,232,0.28)] transition-all active:scale-[0.98] cursor-pointer"
          >
            <Plus className="w-4 h-4 stroke-[2.2]" />
            <span>New engineering task</span>
          </button>
        </div>

        {/* SECTION: Recent */}
        <div className="border-t border-[#EEF2F6] first:border-none">
          <button
            type="button"
            onClick={() => toggleGroup('recent')}
            className="flex items-center justify-between w-full px-3.5 py-2.5 text-left text-[11.5px] font-bold uppercase tracking-wider text-slate-800 hover:text-slate-950 cursor-pointer"
          >
            <span>Recent</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-[#8193A8] transition-transform duration-200 ${
                collapsedGroups.recent ? '-rotate-90' : ''
              }`}
            />
          </button>

          {!collapsedGroups.recent && (
            <div className="px-2 pb-2 space-y-1">
              {/* Optional Search filter if chats exist */}
              {conversations.length > 3 && (
                <div className="px-1.5 pb-1">
                  <div className="relative">
                    <Search className="w-3 h-3 text-[#8193A8] absolute left-2 top-2" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search tasks..."
                      className="w-full pl-6 pr-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded-md text-slate-800 placeholder-slate-400 outline-none focus:border-[#0C64E8]"
                    />
                  </div>
                </div>
              )}

              {filteredChats.length === 0 ? (
                <p className="text-[11.5px] text-[#8193A8] px-2 py-1.5 italic">
                  No engineering tasks yet
                </p>
              ) : (
                <>
                  {displayedChats.map((chat) => {
                    const isActive = activeChatId === chat.id;
                    return (
                      <div
                        key={chat.id}
                        className={`group relative flex items-center gap-2 px-2 py-1.5 rounded-[6px] transition-colors cursor-pointer text-left ${
                          isActive
                            ? 'bg-[#EFF5FE] text-[#0A4FBB]'
                            : 'hover:bg-[#EEF2F7] text-[#44546A]'
                        }`}
                        onClick={() => onSelectChat(chat.id)}
                      >
                        <span
                          className={`w-6 h-6 rounded-[6px] flex items-center justify-center shrink-0 ${
                            isActive
                              ? 'bg-white text-[#0C64E8] shadow-2xs'
                              : 'bg-[#EEF2F7] text-[#8193A8]'
                          }`}
                        >
                          <MessageSquare className="w-3 h-3" />
                        </span>

                        {renamingChatId === chat.id ? (
                          <input
                            type="text"
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleSaveRename(chat.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveRename(chat.id);
                              if (e.key === 'Escape') setRenamingChatId(null);
                            }}
                            className="flex-1 text-xs bg-white border border-blue-400 rounded px-1.5 py-0.5 outline-none font-medium"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="flex-1 min-w-0 text-[12.5px] font-medium truncate">
                            {chat.title}
                          </span>
                        )}

                        {/* Timestamp */}
                        <span className="text-[10.5px] text-[#8193A8] shrink-0 font-mono">
                          {chat.timestamp || '2h'}
                        </span>

                        {/* 3-Dot Context Menu */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenChatId(menuOpenChatId === chat.id ? null : chat.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-slate-900 rounded transition-opacity"
                          title="Options"
                        >
                          <MoreVertical className="w-3.5 h-3.5 text-[#8193A8]" />
                        </button>

                        {menuOpenChatId === chat.id && (
                          <div
                            ref={menuRef}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-1 top-8 z-50 w-36 bg-white rounded-lg shadow-xl border border-slate-200 py-1 text-xs text-slate-700 animate-in fade-in"
                          >
                            <button
                              type="button"
                              onClick={() => handleStartRename(chat)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left"
                            >
                              <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                              <span>Rename</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => onPinChat?.(chat.id)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left"
                            >
                              <Pin className="w-3.5 h-3.5 text-slate-400" />
                              <span>{chat.isPinned ? 'Unpin' : 'Pin'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleShare(chat)}
                              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left"
                            >
                              <Share2 className="w-3.5 h-3.5 text-slate-400" />
                              <span>Share</span>
                            </button>
                            <div className="h-px bg-slate-100 my-1" />
                            <button
                              type="button"
                              onClick={() => {
                                setMenuOpenChatId(null);
                                onDeleteChat(chat.id);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 text-red-600 text-left"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Show more / Show less button if more than 4 chats */}
                  {filteredChats.length > 4 && (
                    <button
                      type="button"
                      onClick={() => setIsChatsExpanded(!isChatsExpanded)}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 mt-1 text-xs font-semibold text-[#0C64E8] hover:bg-[#EFF5FE] rounded-md transition-colors cursor-pointer"
                    >
                      <span>
                        {isChatsExpanded ? 'Show less' : `Show ${filteredChats.length - 4} more`}
                      </span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-[#0C64E8] transition-transform duration-200 ${
                          isChatsExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* SECTION: Engineers */}
        <div className="border-t border-[#EEF2F6]">
          <button
            type="button"
            onClick={() => toggleGroup('eng')}
            className="flex items-center justify-between w-full px-3.5 py-2.5 text-left text-[11.5px] font-bold uppercase tracking-wider text-slate-800 hover:text-slate-950 cursor-pointer"
          >
            <span>Engineers</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-[#8193A8] transition-transform duration-200 ${
                collapsedGroups.eng ? '-rotate-90' : ''
              }`}
            />
          </button>

          {!collapsedGroups.eng && (
            <div className="px-2 pb-1 space-y-1">
              {displayedEngineers.map((e) => {
                const tone = TONE_STYLES[e.tone] || TONE_STYLES.blue;
                const Icon = e.icon;
                const inChain = activeChainSet ? activeChainSet.has(e.id) : null;
                const isDim = activeChainSet !== null && !inChain;

                return (
                  <div
                    key={e.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-[6px] transition-all ${
                      inChain ? 'bg-[#EFF5FE]' : ''
                    } ${isDim ? 'opacity-40' : 'opacity-100'}`}
                  >
                    <div
                      className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center shrink-0"
                      style={{ backgroundColor: tone.bg, color: tone.fg }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <span
                      className={`flex-1 min-w-0 text-[13px] truncate ${
                        inChain ? 'font-semibold text-[#0A4FBB]' : 'font-medium text-[#44546A]'
                      }`}
                    >
                      {e.name}
                    </span>

                    {e.avail ? (
                      <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#0C8A74] bg-[#E4F5EF] px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#11A389]" />
                        Available
                      </span>
                    ) : (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => onOpenHireModal?.(e.id, 'rent')}
                          className="text-[10.5px] font-semibold tracking-wide px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:border-[#C6D6EE] text-[#0C64E8] bg-[#EFF5FE] transition-colors cursor-pointer"
                        >
                          Rent
                        </button>
                        <button
                          type="button"
                          onClick={() => onOpenHireModal?.(e.id, 'hire')}
                          className="text-[10.5px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-[#0C64E8] hover:bg-[#0A4FBB] text-white transition-colors cursor-pointer"
                        >
                          Hire
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {!activeChain && (
                <button
                  type="button"
                  onClick={() => setIsEngExpanded(!isEngExpanded)}
                  className="w-full text-center py-1.5 mt-1 text-xs font-semibold text-[#0C64E8] hover:bg-[#EFF5FE] rounded-md transition-colors cursor-pointer"
                >
                  {isEngExpanded ? 'Show fewer' : `Show ${ENGINEERS_DATA.length - 3} more`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* SECTION: Connectors */}
        <div className="border-t border-[#EEF2F6]">
          <button
            type="button"
            onClick={() => toggleGroup('conn')}
            className="flex items-center justify-between w-full px-3.5 py-2.5 text-left text-[11.5px] font-bold uppercase tracking-wider text-slate-800 hover:text-slate-950 cursor-pointer"
          >
            <span>Connectors</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-[#8193A8] transition-transform duration-200 ${
                collapsedGroups.conn ? '-rotate-90' : ''
              }`}
            />
          </button>

          {!collapsedGroups.conn && (
            <div className="px-3 pb-2 space-y-2">
              <div className="space-y-1.5">
                {DEFAULT_CONNECTORS.map((c) => (
                  <div key={c.name} className="flex items-center gap-2 text-[12.5px] text-[#44546A]">
                    <span
                      className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center text-[9.5px] font-bold text-white shrink-0 tracking-tight"
                      style={{ backgroundColor: c.color }}
                    >
                      {c.mono}
                    </span>
                    <span className="flex-1 min-w-0 truncate">{c.name}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#11A389] shrink-0" title="Connected" />
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={onOpenConnectors}
                className="w-full flex items-center justify-center gap-1.5 py-2 px-3 text-[12.5px] font-semibold text-[#0C64E8] bg-[#EFF5FE] hover:bg-[#E3EEFD] border border-[#E3EEFD] rounded-lg transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add connector</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 3. Rail Footer / User Profile & Account Popover */}
      <div className="p-3 border-t border-slate-200 shrink-0 relative" ref={acctRef}>
        <button
          type="button"
          onClick={() => setIsAcctOpen(!isAcctOpen)}
          className="w-full flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-[#EEF2F7] transition-colors cursor-pointer text-left"
        >
          <div className="w-[30px] h-[30px] rounded-full shrink-0 flex items-center justify-center text-white text-[13px] font-semibold bg-gradient-to-br from-[#E86A9A] to-[#A86BD8] shadow-2xs">
            {initialLetter}
          </div>
          <div className="flex-1 min-w-0 flex items-baseline gap-1.5">
            <b className="text-[13px] font-semibold text-slate-900 truncate">{displayName}</b>
            <span className="text-xs text-[#8193A8]">· Pro</span>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-[#8193A8] shrink-0" />
        </button>

        {/* Account Menu Popover */}
        {isAcctOpen && (
          <div className="absolute left-2 right-2 bottom-16 bg-white rounded-xl shadow-2xl border border-slate-200 p-1.5 z-50 text-xs text-slate-800 animate-in fade-in slide-in-from-bottom-2">
            <div className="px-3 py-2 text-[11px] text-[#8193A8] truncate border-b border-slate-100">
              {currentUser?.email || 'rahul@dashnode.ai'}
            </div>

            <div className="py-1">
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <Settings className="w-4 h-4 text-[#44546A]" />
                <span>Settings</span>
              </button>
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <Activity className="w-4 h-4 text-[#44546A]" />
                <span>Usage</span>
              </button>
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <Globe className="w-4 h-4 text-[#44546A]" />
                <span>Language</span>
              </button>
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <HelpCircle className="w-4 h-4 text-[#44546A]" />
                <span>Get help</span>
              </button>
            </div>

            <div className="h-px bg-slate-100 my-1" />

            <div className="py-1">
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <ArrowUpCircle className="w-4 h-4 text-[#44546A]" />
                <span>Upgrade plan</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAcctOpen(false);
                  onOpenConnectors?.();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <Plug className="w-4 h-4 text-[#44546A]" />
                <span>Connectors</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAcctOpen(false);
                  onToggleMemory?.();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <Brain className="w-4 h-4 text-[#44546A]" />
                <span>Memory</span>
              </button>
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <Info className="w-4 h-4 text-[#44546A]" />
                <span>Learn more</span>
              </button>
              <button
                type="button"
                onClick={() => setIsAcctOpen(false)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-[#EEF2F7] text-left font-medium"
              >
                <LifeBuoy className="w-4 h-4 text-[#44546A]" />
                <span>Support</span>
              </button>
            </div>

            <div className="h-px bg-slate-100 my-1" />

            <button
              type="button"
              onClick={() => {
                setIsAcctOpen(false);
                if (currentUser) onSignOut?.();
                else onOpenAuth?.('login');
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-red-50 text-red-600 text-left font-medium"
            >
              <LogOut className="w-4 h-4" />
              <span>{currentUser ? 'Log out' : 'Log in'}</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
